/**
 * BrandedChakraProvider - Portable themed ChakraProvider.
 *
 * Same logic as ThemeProvider but without Next.js dependencies
 * (no useRouter, no next-themes, no Toaster). Takes a LayoutThemeConfig
 * prop directly and provides a branded ChakraProvider + Google Font loading.
 *
 * Custom fonts are baked into the Chakra system tokens (not overridden via
 * inline CSS), so the system's own CSS variables are correct from the start.
 *
 * Use this anywhere you need branded Chakra theming outside the main
 * Next.js app tree (e.g., iframes, isolated renders).
 */

import { type ReactNode, useMemo } from "react";
/* eslint-disable-next-line no-restricted-imports */
import { ChakraProvider } from "@chakra-ui/react";
import generateTheme, { defaultSystem } from "@shared/theme/theme";
import type {
  ThemeComponentOverrides,
  ThemeTokenOverrides,
} from "@shared/theme/types/theme-overrides";
import {
  colorToPalette,
  validateColorString,
} from "@shared/theme/utils/theme-generator";
import {
  getFontFamilyName,
  getBrandingFontLinks,
} from "@shared/modules/branding/utils/font-spec";
import type { LayoutThemeConfig } from "@shared/config/layout.config";

interface BrandedChakraProviderProps {
  children: ReactNode;
  themeConfig: LayoutThemeConfig | undefined;
  websiteThemeConfig?: {
    tokens?: ThemeTokenOverrides;
    components?: ThemeComponentOverrides;
  };
}

export function BrandedChakraProvider({
  children,
  themeConfig,
  websiteThemeConfig,
}: BrandedChakraProviderProps): JSX.Element {
  const themePrimary = validateColorString(themeConfig?.primaryColor);
  const themeSecondary = validateColorString(themeConfig?.secondaryColor);
  const headingFontSpec = themeConfig?.headingFont;
  const bodyFontSpec = themeConfig?.bodyFont;

  // Parse font specs - strip weight info for CSS font-family, keep full spec for URL
  const headingFontName = headingFontSpec
    ? getFontFamilyName(headingFontSpec)
    : undefined;
  const bodyFontName = bodyFontSpec
    ? getFontFamilyName(bodyFontSpec)
    : undefined;

  // Generate a Chakra system with custom colors AND fonts baked in.
  // Fonts are part of the system tokens, so Chakra generates the correct
  // --chakra-fonts-heading/body CSS variables directly.
  const system = useMemo(() => {
    const hasCustomColors = !!themePrimary || !!themeSecondary;
    const hasCustomFonts = !!headingFontName || !!bodyFontName;
    const hasThemeOverrides =
      !!websiteThemeConfig?.tokens || !!websiteThemeConfig?.components;

    if (!hasCustomColors && !hasCustomFonts && !hasThemeOverrides) {
      return defaultSystem;
    }

    return generateTheme({
      primaryColor: themePrimary ? colorToPalette(themePrimary) : undefined,
      secondaryColor: themeSecondary
        ? colorToPalette(themeSecondary)
        : undefined,
      headingFont: headingFontName,
      bodyFont: bodyFontName,
      tokens: websiteThemeConfig?.tokens,
      components: websiteThemeConfig?.components,
    });
  }, [
    themePrimary,
    themeSecondary,
    headingFontName,
    bodyFontName,
    websiteThemeConfig?.tokens,
    websiteThemeConfig?.components,
  ]);

  const extraFontNames = useMemo(() => {
    const names: string[] = [];
    const buttonFont = websiteThemeConfig?.components?.button?.fontFamily;
    if (buttonFont) names.push(buttonFont);
    return names;
  }, [websiteThemeConfig?.components?.button?.fontFamily]);

  const fontLinks = useMemo(
    () => getBrandingFontLinks(headingFontSpec, bodyFontSpec, extraFontNames),
    [headingFontSpec, bodyFontSpec, extraFontNames]
  );

  return (
    <ChakraProvider value={system}>
      {fontLinks.googleFontUrl && (
        <>
          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link
            rel="preconnect"
            href="https://fonts.gstatic.com"
            crossOrigin="anonymous"
          />
          <link rel="stylesheet" href={fontLinks.googleFontUrl} />
        </>
      )}
      {fontLinks.adobeProjectIds.length > 0 && (
        <>
          <link rel="preconnect" href="https://use.typekit.net" />
          {fontLinks.adobeProjectIds.map((projectId) => (
            <link
              key={projectId}
              rel="stylesheet"
              href={`https://use.typekit.net/${projectId}.css`}
            />
          ))}
        </>
      )}
      {children}
    </ChakraProvider>
  );
}
